import { Plus, Check } from 'lucide-react';
import type { FilamentWithRelations } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { ColorSwatches } from './ColorSwatches';

interface FilamentCardProps {
  filament: FilamentWithRelations;
  isInLibrary?: boolean;
  onAdd?: () => void;
  onView?: () => void;
  loading?: boolean;
}

export function FilamentCard({
  filament,
  isInLibrary = false,
  onAdd,
  onView,
  loading = false,
}: FilamentCardProps) {
  return (
    <div
      className="group relative rounded-lg border border-border bg-background-subtle p-4 transition-colors hover:border-border-hover"
      onClick={onView}
      role={onView ? 'button' : undefined}
      tabIndex={onView ? 0 : undefined}
      onKeyDown={onView ? (e) => e.key === 'Enter' && onView() : undefined}
    >
      {/* Color and Basic Info */}
      <div className="flex items-start gap-3">
        <ColorSwatches colors={filament.colorHexes} size="lg" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-medium text-foreground">
            {filament.name}
          </h3>
          <p className="truncate text-xs text-foreground-muted">
            {filament.manufacturer?.name}
          </p>
        </div>
      </div>

      {/* Details */}
      <div className="mt-3 flex flex-wrap gap-2">
        <span className="inline-flex items-center rounded bg-background px-2 py-0.5 text-xs text-foreground-muted">
          {filament.type?.name}
        </span>
        <span className="inline-flex items-center rounded bg-background px-2 py-0.5 text-xs text-foreground-muted">
          {filament.diameter}mm
        </span>
        {filament.finish && (
          <span className="inline-flex items-center rounded bg-background px-2 py-0.5 text-xs text-foreground-muted">
            {filament.finish.name}
          </span>
        )}
      </div>

      {/* Color Name */}
      <p className="mt-2 text-xs text-foreground-muted">{filament.colorName}</p>

      {/* Add to Library Button */}
      {onAdd && !isInLibrary && (
        <div className="mt-3">
          <Button
            size="sm"
            variant="secondary"
            className="w-full"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            loading={loading}
          >
            Add to My Library
          </Button>
        </div>
      )}

      {/* In Library Indicator */}
      {isInLibrary && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-success">
          <Check className="h-3.5 w-3.5" />
          <span>In your library</span>
        </div>
      )}
    </div>
  );
}
